<template>
  <div>
    <ConnectionStatusBanner />
    <LoadingSplash :visible="loading" />
    <Header />
    <UNotifications />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <NotifyPermissionModal v-if="showNotifyModal && !suppressGlobalPrompts" @close="onNotifyClose" @allow="onNotifyAllow" />
    <PWAInstallModal v-if="!suppressGlobalPrompts" ref="pwaInstallModalRef" @installed="onPWAInstalled" @notified="onPWANotified" @closed="onPWAClosed" />
  </div>
</template>
<script setup>
import PWAInstallModal from '~/components/PWAInstallModal.vue'
import { usePushNotifications } from '~/composables/usePushNotifications'
import { isOrderFormRoute } from '~/utils/global-prompts.mjs'

const loading = ref(true)
const nuxtApp = useNuxtApp()
const route = useRoute()
const showNotifyModal = ref(false)
const pwaInstallModalRef = ref(null)
const suppressGlobalPrompts = computed(() => (
  route.matched.some(record => record.meta.suppressGlobalPrompts === true) ||
  isOrderFormRoute(route.path)
))

nuxtApp.hook('page:start', () => {
  loading.value = true
})

nuxtApp.hook('page:finish', () => {
  console.info('[push-sync] page finished, syncing subscription...')
  loading.value = false
  syncPushSubscription()
})

// Keep a very short safety timeout only so the splash can render on initial load
// without blocking content for 3 seconds.
let safetyTimer = null
let notifyTimer = null

const scheduleNotifyPrompt = () => {
  if (!process.client || suppressGlobalPrompts.value || notifyTimer || localStorage.getItem('notify-prompt-dismissed')) return

  notifyTimer = setTimeout(() => {
    notifyTimer = null
    if (!suppressGlobalPrompts.value && 'Notification' in window && Notification.permission === 'default') {
      showNotifyModal.value = true
    }
  }, 3000)
}

onMounted(() => {
  safetyTimer = setTimeout(() => {
    loading.value = false
  }, 400)
  scheduleNotifyPrompt()
})

watch(() => route.fullPath, () => {
  if (suppressGlobalPrompts.value) {
    showNotifyModal.value = false
    if (notifyTimer) {
      clearTimeout(notifyTimer)
      notifyTimer = null
    }
    return
  }

  scheduleNotifyPrompt()
})

const onNotifyClose = () => {
  showNotifyModal.value = false
  localStorage.setItem('notify-prompt-dismissed', 'true')
}

const getPushClient = () => {
  const { authenticated, user, token } = useAuth()
  const currentUser = authenticated.value ? user.value : null
  return usePushNotifications({
    applicationServerKey: useRuntimeConfig().public?.vapidPublicKey,
    uid: currentUser?.id || currentUser?._id || null,
  })
}

const onNotifyAllow = async () => {
  showNotifyModal.value = false
  if (process.client) {
    try {
      await getPushClient().subscribe()
    } catch (e) {
      console.error('Push subscription failed:', e)
    }
  }
}

const PUSH_SYNC_MIN_INTERVAL = 30000
let lastPushSync = 0

const syncPushSubscription = async () => {
  if (!process.client || !('serviceWorker' in navigator)) {
    console.debug('[push-sync] skipped: no service worker support')
    return
  }

  const now = Date.now()
  if (now - lastPushSync < PUSH_SYNC_MIN_INTERVAL) {
    console.debug('[push-sync] skipped: throttled')
    return
  }
  lastPushSync = now

  try {
    const subscription = await getPushClient().updateSubscription()
    console.info('[push-sync] done:', subscription ? 'subscription synced to server' : 'no active subscription (too slow or no permission)')
  } catch (e) {
    console.warn('[push-sync] auto-sync failed:', e)
  }
}

const onPWAInstalled = (accepted) => {
  console.log('PWA install:', accepted ? 'accepted' : 'ignored')
}

const onPWANotified = (permission) => {
  console.log('Notification permission:', permission)
}

const onPWAClosed = () => {
  console.log('PWA install modal closed')
}

onBeforeUnmount(() => {
  if (safetyTimer) clearTimeout(safetyTimer)
  if (notifyTimer) clearTimeout(notifyTimer)
})
</script>
